/**
 * LQIP (Low Quality Image Placeholder) Utilities
 * Advanced utilities for generating and managing low quality image placeholders
 */

export interface LQIPConfig {
  width?: number;
  height?: number;
  quality?: number;
  blur?: number;
  fit?: 'crop' | 'contain' | 'cover';
  format?: 'webp' | 'jpeg' | 'png';
  fallbackToOriginal?: boolean;
}

export interface LQIPResult {
  url: string;
  isGenerated: boolean;
  config: LQIPConfig;
  originalUrl: string;
}

/**
 * Default LQIP configuration
 */
export const DEFAULT_LQIP_CONFIG: Required<LQIPConfig> = {
  width: 20,
  height: 15,
  quality: 30,
  blur: 10,
  fit: 'crop',
  format: 'webp',
  fallbackToOriginal: false,
};

/**
 * Generate LQIP URL from image URL
 */
export const generateLQIPUrl = (
  imageUrl: string,
  config: Partial<LQIPConfig> = {},
): LQIPResult => {
  const finalConfig = { ...DEFAULT_LQIP_CONFIG, ...config };

  if (!imageUrl || imageUrl.trim() === '') {
    return {
      url: '',
      isGenerated: false,
      config: finalConfig,
      originalUrl: imageUrl,
    };
  }

  try {
    let cleanUrl = imageUrl.trim();

    // Fix common URL issues
    if (cleanUrl.includes('http:/localhost')) {
      cleanUrl = cleanUrl.replace('http:/localhost', 'http://localhost');
    }

    // Only generate LQIP for local images or URLs that support query params
    const shouldGenerateLQIP =
      cleanUrl.includes('/storage/images/') ||
      cleanUrl.includes('/storage/') ||
      (!cleanUrl.startsWith('http') && cleanUrl) ||
      (cleanUrl.startsWith('http') && cleanUrl.includes(window.location.hostname));

    if (!shouldGenerateLQIP) {
      return {
        url: finalConfig.fallbackToOriginal ? cleanUrl : '',
        isGenerated: false,
        config: finalConfig,
        originalUrl: imageUrl,
      };
    }

    // Ensure absolute URL
    let absoluteUrl = cleanUrl;
    if (!absoluteUrl.startsWith('http')) {
      const baseUrl = window.location.origin;
      absoluteUrl = absoluteUrl.startsWith('/')
        ? `${baseUrl}${absoluteUrl}`
        : `${baseUrl}/${absoluteUrl}`;
    }

    // Generate LQIP URL with parameters
    const url = new URL(absoluteUrl);

    // Add LQIP parameters
    url.searchParams.set('w', finalConfig.width.toString());
    url.searchParams.set('h', finalConfig.height.toString());
    url.searchParams.set('q', finalConfig.quality.toString());
    url.searchParams.set('blur', finalConfig.blur.toString());
    url.searchParams.set('fit', finalConfig.fit);

    // Add format if supported
    if (finalConfig.format !== 'jpeg') {
      url.searchParams.set('fm', finalConfig.format);
    }

    // Add cache buster for better reliability
    url.searchParams.set('lqip', '1');

    return {
      url: url.toString(),
      isGenerated: true,
      config: finalConfig,
      originalUrl: imageUrl,
    };

  } catch (error) {
    console.warn('LQIP generation failed:', error);
    return {
      url: finalConfig.fallbackToOriginal ? imageUrl : '',
      isGenerated: false,
      config: finalConfig,
      originalUrl: imageUrl,
    };
  }
};

/**
 * Generate multiple LQIP variants for different use cases
 */
export const generateLQIPVariants = (imageUrl: string) => {
  return {
    // Tiny blur for instant loading
    micro: generateLQIPUrl(imageUrl, {
      width: 10,
      height: 8,
      quality: 20,
      blur: 15,
    }),

    // Small blur for better preview
    small: generateLQIPUrl(imageUrl, {
      width: 20,
      height: 15,
      quality: 30,
      blur: 10,
    }),

    // Medium quality for slow connections
    medium: generateLQIPUrl(imageUrl, {
      width: 40,
      height: 30,
      quality: 50,
      blur: 5,
    }),
  };
};

/**
 * Smart LQIP selection based on connection speed
 */
export const getOptimalLQIP = (imageUrl: string): LQIPResult => {
  // Check connection speed if available
  const connection = (navigator as any)?.connection;

  if (connection) {
    const effectiveType = connection.effectiveType;

    switch (effectiveType) {
      case 'slow-2g':
      case '2g':
        return generateLQIPUrl(imageUrl, {
          width: 8,
          height: 6,
          quality: 15,
          blur: 20,
        });

      case '3g':
        return generateLQIPUrl(imageUrl, {
          width: 15,
          height: 12,
          quality: 25,
          blur: 12,
        });

      case '4g':
      default:
        return generateLQIPUrl(imageUrl, {
          width: 25,
          height: 18,
          quality: 35,
          blur: 8,
        });
    }
  }

  // Default LQIP if connection info not available
  return generateLQIPUrl(imageUrl);
};

/**
 * Base64 LQIP generation for critical images
 */
export const generateBase64LQIP = async (imageUrl: string): Promise<string> => {
  try {
    // Generate tiny LQIP URL
    const lqipResult = generateLQIPUrl(imageUrl, {
      width: 8,
      height: 6,
      quality: 10,
      blur: 20,
    });

    if (!lqipResult.isGenerated) {
      return '';
    }

    // Fetch and convert to base64
    const response = await fetch(lqipResult.url);
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });

  } catch (error) {
    console.warn('Base64 LQIP generation failed:', error);
    return '';
  }
};

/**
 * LQIP cache management
 */
class LQIPCache {
  private cache = new Map<string, string>();
  private maxSize = 100;

  set(key: string, value: string): void {
    // Simple LRU: remove oldest if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  get(key: string): string | undefined {
    const value = this.cache.get(key);
    if (value) {
      // Move to end (LRU)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }
}

export const lqipCache = new LQIPCache();

/**
 * Enhanced LQIP with caching
 */
export const getCachedLQIP = (imageUrl: string, config?: Partial<LQIPConfig>): LQIPResult => {
  const cacheKey = `${imageUrl}-${JSON.stringify(config || {})}`;
  const cached = lqipCache.get(cacheKey);

  if (cached) {
    return JSON.parse(cached);
  }

  const result = generateLQIPUrl(imageUrl, config);
  lqipCache.set(cacheKey, JSON.stringify(result));

  return result;
};

/**
 * Preload LQIP for better performance
 */
export const preloadLQIP = (imageUrl: string, config?: Partial<LQIPConfig>): Promise<void> => {
  return new Promise((resolve, reject) => {
    const lqipResult = generateLQIPUrl(imageUrl, config);

    if (!lqipResult.isGenerated) {
      resolve();
      return;
    }

    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to preload LQIP: ${lqipResult.url}`));
    img.src = lqipResult.url;
  });
};

/**
 * Batch preload multiple LQIPs
 */
export const preloadLQIPs = async (
  imageUrls: string[],
  config?: Partial<LQIPConfig>,
  maxConcurrent = 3,
): Promise<void> => {
  const chunks: string[][] = [];

  // Split URLs into chunks for concurrent processing
  for (let i = 0; i < imageUrls.length; i += maxConcurrent) {
    chunks.push(imageUrls.slice(i, i + maxConcurrent));
  }

  try {
    for (const chunk of chunks) {
      await Promise.allSettled(
        chunk.map(url => preloadLQIP(url, config))
      );
    }
  } catch (error) {
    console.warn('Batch LQIP preload failed:', error);
  }
};

/**
 * LQIP quality detector
 */
export const detectLQIPSupport = async (): Promise<{
  webp: boolean;
  blur: boolean;
  queryParams: boolean;
}> => {
  const testUrl = `${window.location.origin}/img/noimg.jpg`;

  const results = {
    webp: false,
    blur: false,
    queryParams: false,
  };

  try {
    // Test WebP support
    const webpTest = await fetch(`${testUrl}?fm=webp&w=1&h=1`);
    results.webp = webpTest.ok;

    // Test blur support
    const blurTest = await fetch(`${testUrl}?blur=5&w=1&h=1`);
    results.blur = blurTest.ok;

    // Test query params support
    const paramTest = await fetch(`${testUrl}?w=1&h=1`);
    results.queryParams = paramTest.ok;

  } catch (error) {
    console.warn('LQIP support detection failed:', error);
  }

  return results;
};

/**
 * CSS-in-JS for LQIP transitions
 */
export const lqipTransitionStyles = {
  container: {
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },

  lqip: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    filter: 'blur(10px)',
    transform: 'scale(1.1)', // Slightly larger to hide blur edges
    transition: 'opacity 0.5s ease-in-out',
  },

  mainImage: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    transition: 'opacity 0.5s ease-in-out',
  },
};

/**
 * LQIP configuration for different image types
 */
export const LQIP_PRESETS = {
  hero: {
    width: 30,
    height: 20,
    quality: 40,
    blur: 8,
  },

  thumbnail: {
    width: 15,
    height: 12,
    quality: 25,
    blur: 12,
  },

  avatar: {
    width: 20,
    height: 20,
    quality: 35,
    blur: 10,
  },

  background: {
    width: 40,
    height: 25,
    quality: 30,
    blur: 15,
  },
} as const;

export type LQIPPreset = keyof typeof LQIP_PRESETS;

/**
 * Get LQIP using preset configuration
 */
export const getLQIPWithPreset = (imageUrl: string, preset: LQIPPreset): LQIPResult => {
  return generateLQIPUrl(imageUrl, LQIP_PRESETS[preset]);
};
